import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { formatDate } from '../../lib/utils';
import { apiGet, apiPut } from '../../contexts/AuthContext';
import { Landmark, Globe } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Pending: 'bg-ember/15 text-warning',
  'Under Review': 'bg-brand/15 text-brand-soft',
  Approved: 'bg-brand/15 text-success',
  Rejected: 'bg-ember/15 text-danger',
};

const STATUSES = ['Pending', 'Under Review', 'Approved', 'Rejected'];

export default function AdminLoans() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchLoans = () => {
    setLoadError(false);
    apiGet('/admin/loan-applications')
      .then(r => setLoans(r.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLoans(); }, []);

  const handleStatusChange = async (loanId, newStatus) => {
    try {
      await apiPut(`/admin/loan-applications/${loanId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      setLoans(prev => prev.map(l => l.id === loanId ? { ...l, status: newStatus } : l));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div>
        <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Education Loans</h1>
        <p className="text-ink-2 mt-1">Review and manage loan applications submitted by partners</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STATUSES.map(status => (
          <div key={status} className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-4">
            <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">{status}</p>
            <p className="text-2xl font-semibold text-ink mt-1">{loans.filter(l => l.status === status).length}</p>
          </div>
        ))}
      </div>

      {loadError ? (
        <LoadError what="loan applications" onRetry={() => { setLoading(true); fetchLoans(); }} />
      ) : loans.length > 0 ? (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-contrast/5 border-b border-contrast/10">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Candidate</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Father's Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Father Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Country</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Partner</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => (
                  <tr key={loan.id} className="border-b border-contrast/[0.07] hover:bg-contrast/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-surface-3 rounded-full flex items-center justify-center text-brand text-xs font-semibold">
                          {loan.candidate_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-ink">{loan.candidate_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-2">{loan.fathers_name}</td>
                    <td className="px-4 py-3 text-sm text-ink-2 whitespace-nowrap">{loan.candidate_phone}</td>
                    <td className="px-4 py-3 text-sm text-ink-2 whitespace-nowrap">{loan.father_phone || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-ink-3" />
                        <span className="text-sm text-ink-2">{loan.country_applying_for}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-ink">{loan.partner_name}</p>
                        <p className="text-xs text-ink-3">{loan.partner_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={loan.status} onValueChange={(val) => handleStatusChange(loan.id, val)}>
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-3">{formatDate(loan.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card flex flex-col items-center justify-center py-16">
          <Landmark className="w-12 h-12 text-ink-3 mb-3" />
          <p className="text-ink-3 font-medium">No loan applications yet</p>
          <p className="text-sm text-ink-3 mt-1">Loan applications from partners will appear here</p>
        </div>
      )}
    </div>
  );
}
